import React from "react";
import { Document, Page } from "react-pdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "./LoadingSpinner";
import "../pdfWorkerSetup";

type PublicationPdfViewerProps = {
  fileUrl: string;
  pageNumber: number;
  onPageNumberChange: (page: number) => void;
  numPages: number;
  onDocumentLoadSuccess: (numPages: number) => void;
  renderedPages: Set<number>;
  onPageRenderSuccess: (page: number) => void;
  onLoadError: (error: Error) => void;
  className?: string;
  showExpandButton?: boolean;
  onExpandClick?: () => void;
};

const PublicationPdfViewer: React.FC<PublicationPdfViewerProps> = ({
  fileUrl,
  pageNumber,
  onPageNumberChange,
  numPages,
  onDocumentLoadSuccess,
  renderedPages,
  onPageRenderSuccess,
  onLoadError,
  className,
  showExpandButton = false,
  onExpandClick,
}) => {
  const pdfPagesLoading = numPages === 0 || !renderedPages.has(1);
  const canGoNext =
    pageNumber < numPages && renderedPages.has(pageNumber + 1);
  const canGoPrev = pageNumber > 1 && renderedPages.has(pageNumber - 1);

  const nextPage = () => {
    if (canGoNext) onPageNumberChange(pageNumber + 1);
  };

  const prevPage = () => {
    if (canGoPrev) onPageNumberChange(pageNumber - 1);
  };

  return (
    <div className={["pdf-viewer-container", className].filter(Boolean).join(" ")}>
      {pdfPagesLoading && (
        <div className="pdf-viewer-loading">
          <LoadingSpinner />
        </div>
      )}
      <div className="pdf-document-frame">
        {showExpandButton && onExpandClick && (
          <button
            type="button"
            className="pdf-fullview-btn"
            onClick={onExpandClick}
            disabled={pdfPagesLoading}
            aria-label="Open full view"
            title="Full view"
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        )}
        <Document
          className="react-pdf__Document"
          file={fileUrl}
          onLoadSuccess={({ numPages: totalPages }) =>
            onDocumentLoadSuccess(totalPages)
          }
          onLoadError={onLoadError}
        >
          {numPages > 0 && (
            <div className="pdf-pages-viewport">
              {Array.from({ length: numPages }, (_, index) => {
              const currentPage = index + 1;
              const isActive = currentPage === pageNumber;

              return (
                <div
                  key={`page_${currentPage}`}
                  className={`pdf-page-layer${isActive ? " pdf-page-layer--active" : ""}`}
                  aria-hidden={!isActive}
                >
                  <Page
                    className="publication-pdf-page"
                    pageNumber={currentPage}
                    onRenderSuccess={() => onPageRenderSuccess(currentPage)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              );
            })}
          </div>
        )}
        </Document>
      </div>

      <div className="pdf-navigation">
        <button className="btn" onClick={prevPage} disabled={!canGoPrev}>
          Previous
        </button>
        <span>
          Page {pageNumber} of {numPages || "…"}
        </span>
        <button className="btn" onClick={nextPage} disabled={!canGoNext}>
          Next
        </button>
      </div>
    </div>
  );
};

export default PublicationPdfViewer;
